import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaArrowRight, FaArrowUp, FaArrowDown, FaCalendarAlt, FaBox, FaExchangeAlt, FaUndo, FaTimes, FaExclamationTriangle, FaSearch, FaFilter } from 'react-icons/fa';

const StockMovementReport = () => {
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('cards');
    const [isMobile, setIsMobile] = useState(false);
    const [filter, setFilter] = useState('all');
    const [produtoFilter, setProdutoFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [reverseLoading, setReverseLoading] = useState({});
    const [modalConfirmacao, setModalConfirmacao] = useState({
        isOpen: false,
        movimentacao: null
    });

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        try {
            setLoading(true);
            
            // Carrega movimentações e produtos em paralelo
            const [movimentacoesResponse, produtosResponse] = await Promise.all([
                api.get('/movimentacoes'),
                api.get('/produtos')
            ]);
            
            setMovimentacoes(movimentacoesResponse.data);
            setProdutos(produtosResponse.data);
            setError(null);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            setError("Erro ao carregar dados");
        } finally {
            setLoading(false);
        }
    };

    const carregarMovimentacoes = async () => {
        try {
            setLoading(true);
            const response = await api.get('/movimentacoes');
            setMovimentacoes(response.data);
            setError(null);
        } catch (error) {
            console.error("Erro ao buscar movimentações:", error);
            setError("Erro ao carregar movimentações");
        } finally {
            setLoading(false);
        }
    };

    const abrirModalConfirmacao = (movimentacao) => {
        setModalConfirmacao({
            isOpen: true,
            movimentacao: movimentacao
        });
    };

    const fecharModalConfirmacao = () => {
        setModalConfirmacao({
            isOpen: false,
            movimentacao: null
        });
    };

    const reverterMovimentacao = async () => {
        if (!modalConfirmacao.movimentacao) return;

        const movimentacaoId = modalConfirmacao.movimentacao.id;

        try {
            setReverseLoading(prev => ({ ...prev, [movimentacaoId]: true }));
            
            const response = await api.post(`/movimentacoes/${movimentacaoId}/reverter`);
            
            // Mostra mensagem de sucesso
            const mensagem = response.data.mensagem || 'Movimentação revertida com sucesso!';
            alert(mensagem);
            
            // Fecha o modal
            fecharModalConfirmacao();
            
            // Recarrega as movimentações
            await carregarMovimentacoes();
            
        } catch (error) {
            console.error("Erro ao reverter movimentação:", error);
            const mensagemErro = error.response?.data?.erro || 'Erro ao reverter movimentação';
            alert(mensagemErro);
        } finally {
            setReverseLoading(prev => ({ ...prev, [movimentacaoId]: false }));
        }
    };

    // Função para ajustar o fuso horário corretamente
    const ajustarFusoHorario = (dataISO) => {
        if (!dataISO) return "Data inválida";

        try {
            let data = new Date(dataISO);

            if (isNaN(data.getTime())) {
                return "Data inválida";
            }

            return data.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
        } catch (error) {
            console.error("Erro ao converter data:", error);
            return "Data inválida";
        }
    };

    // Formatar data para mobile (mais compacto)
    const formatarDataMobile = (dataISO) => {
        if (!dataISO) return "N/A";

        try {
            const data = new Date(dataISO);
            if (isNaN(data.getTime())) return "N/A";

            return data.toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        } catch (error) {
            return "N/A";
        }
    };

    // Filtrar movimentações
    const movimentacoesFiltradas = movimentacoes.filter(mov => {
        // Filtro por tipo
        if (filter !== 'all' && mov.tipo !== filter) return false;
        
        // Filtro por produto
        if (produtoFilter !== 'all' && mov.produto_id !== parseInt(produtoFilter)) return false;
        
        // Filtro por termo de busca
        if (searchTerm && !mov.produto_nome.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        
        return true;
    });

    // Limpar todos os filtros
    const limparFiltros = () => {
        setFilter('all');
        setProdutoFilter('all');
        setSearchTerm('');
    };

    // Componente Card para Movimentação
    const MovimentacaoCard = ({ movimentacao }) => {
        const isEntrada = movimentacao.tipo === 'entrada';
        const isRevertida = movimentacao.revertida;
        const isReversao = movimentacao.movimentacao_original_id !== null;
        const podeReverter = movimentacao.pode_reverter && !isRevertida && !isReversao;
        
        const colors = isEntrada 
            ? { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' }
            : { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };

        return (
            <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4 mb-4 ${isRevertida ? 'opacity-60' : ''}`}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-800">{movimentacao.produto_nome}</h3>
                            {isRevertida && (
                                <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                                    REVERTIDA
                                </span>
                            )}
                            {isReversao && (
                                <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                                    REVERSÃO
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                            <FaCalendarAlt size={12} />
                            <span>{formatarDataMobile(movimentacao.data_hora)}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isEntrada ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {isEntrada ? 'Entrada' : 'Saída'}
                        </div>
                        {podeReverter && (
                            <button
                                onClick={() => abrirModalConfirmacao(movimentacao)}
                                disabled={reverseLoading[movimentacao.id]}
                                className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded-full transition-colors"
                                title="Reverter movimentação"
                            >
                                {reverseLoading[movimentacao.id] ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                                ) : (
                                    <FaUndo size={14} />
                                )}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                            <FaBox size={10} />
                            <span>Quantidade</span>
                        </div>
                        <div className="font-bold text-gray-800">{movimentacao.quantidade} un</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                            <FaExchangeAlt size={10} />
                            <span>Variação</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-800 text-sm">
                            <span>{movimentacao.estoque_antes ?? "N/D"}</span>
                            <span className="text-gray-400">→</span>
                            <span className={`font-bold ${colors.text}`}>{movimentacao.estoque_depois ?? "N/D"}</span>
                        </div>
                    </div>
                </div>

                {movimentacao.motivo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                            <span className="font-medium">Motivo:</span> {movimentacao.motivo}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-gray-600">Carregando movimentações...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="text-red-500 mb-2">
                        <FaExchangeAlt size={24} />
                    </div>
                    <p className="text-red-600 font-medium">{error}</p>
                    <button 
                        onClick={carregarDados}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    // Contadores
    const entradas = movimentacoesFiltradas.filter(m => m.tipo === 'entrada').length;
    const saidas = movimentacoesFiltradas.filter(m => m.tipo === 'saida').length;

    return (
        <>
            <div className="max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <div>
                        <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2 md:mb-0">
                            Relatório de Movimentação
                        </h2>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                            <span className="bg-gray-100 px-2 py-1 rounded">
                                Total: {movimentacoesFiltradas.length}
                            </span>
                            {entradas > 0 && (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                                    {entradas} entradas
                                </span>
                            )}
                            {saidas > 0 && (
                                <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                                    {saidas} saídas
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                        {!isMobile && (
                            <>
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={`px-4 py-2 rounded-lg ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    Tabela
                                </button>
                                <button
                                    onClick={() => setViewMode('cards')}
                                    className={`px-4 py-2 rounded-lg ${viewMode === 'cards' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    Cards
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Barra de Pesquisa */}
                <div className="mb-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Buscar por nome do produto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filtros */}
                <div className="mb-4 space-y-3">
                    {/* Filtro por tipo */}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <FaFilter size={12} />
                            Tipo:
                        </span>
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1 rounded-lg text-sm ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            Todas
                        </button>
                        <button
                            onClick={() => setFilter('entrada')}
                            className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${filter === 'entrada' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            <FaArrowUp size={10} />
                            Entradas
                        </button>
                        <button
                            onClick={() => setFilter('saida')}
                            className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${filter === 'saida' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            <FaArrowDown size={10} />
                            Saídas
                        </button>
                    </div>

                    {/* Filtro por produto */}
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <FaBox size={12} />
                            Produto:
                        </span>
                        <select
                            value={produtoFilter}
                            onChange={(e) => setProdutoFilter(e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Todos os produtos</option>
                            {produtos
                                .sort((a, b) => a.nome.localeCompare(b.nome))
                                .map(produto => (
                                    <option key={produto.id} value={produto.id}>
                                        {produto.nome}
                                    </option>
                                ))
                            }
                        </select>

                        {/* Botão para limpar filtros */}
                        {(filter !== 'all' || produtoFilter !== 'all' || searchTerm) && (
                            <button
                                onClick={limparFiltros}
                                className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 flex items-center gap-1"
                            >
                                <FaTimes size={10} />
                                Limpar
                            </button>
                        )}
                    </div>
                </div>

                {/* Visualização Condicional */}
                {movimentacoesFiltradas.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <FaExchangeAlt className="mx-auto mb-2" size={24} />
                        <p>Nenhuma movimentação encontrada</p>
                        {(filter !== 'all' || produtoFilter !== 'all' || searchTerm) && (
                            <p className="text-sm mt-2">Tente ajustar os filtros de busca</p>
                        )}
                    </div>
                ) : viewMode === 'table' ? (
                    <div className="overflow-x-auto -mx-4 md:mx-0">
                        <div className="inline-block min-w-full align-middle px-4 md:px-0">
                            <table className="min-w-full border-collapse border border-gray-300 font-light">
                                <thead>
                                    <tr style={{ backgroundColor: "#5B4740", color: "#FFF7F5" }}>
                                        <th className="p-2 text-left">Produto</th>
                                        <th className="p-2 text-center md:table-cell hidden">Data/Hora</th>
                                        <th className="p-2 text-center">Tipo</th>
                                        <th className="p-2 text-center">Qtd</th>
                                        <th className="p-2 text-center md:table-cell hidden">Variação</th>
                                        <th className="p-2 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movimentacoesFiltradas.map(mov => {
                                        const podeReverter = mov.pode_reverter && !mov.revertida && !mov.movimentacao_original_id;
                                        
                                        return (
                                            <tr key={mov.id} className={`border border-gray-300 hover:bg-gray-50 ${mov.revertida ? 'opacity-60' : ''}`}>
                                                <td className="p-2">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold">{mov.produto_nome}</span>
                                                            {mov.revertida && (
                                                                <span className="bg-gray-500 text-white text-xs px-1 py-0.5 rounded">
                                                                    REV
                                                                </span>
                                                            )}
                                                            {mov.movimentacao_original_id && (
                                                                <span className="bg-purple-500 text-white text-xs px-1 py-0.5 rounded">
                                                                    REV
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 md:hidden">
                                                            {formatarDataMobile(mov.data_hora)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center text-sm md:table-cell hidden whitespace-nowrap">
                                                    {ajustarFusoHorario(mov.data_hora)}
                                                </td>
                                                <td className="p-2 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                        mov.tipo === "saida" ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                                                    }`}>
                                                        {mov.tipo === "saida" ? 
                                                            isMobile ? "S" : "Saída" : 
                                                            isMobile ? "E" : "Entrada"}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-center font-bold">{mov.quantidade}</td>
                                                <td className="p-2 text-center md:table-cell hidden">
                                                    <div className="flex items-center justify-center gap-1 text-sm">
                                                        <span>{mov.estoque_antes ?? "N/D"}</span>
                                                        <FaArrowRight className="text-gray-400" size={12} />
                                                        <span className={`font-bold ${mov.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {mov.estoque_depois ?? "N/D"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    {podeReverter && (
                                                        <button
                                                            onClick={() => abrirModalConfirmacao(mov)}
                                                            disabled={reverseLoading[mov.id]}
                                                            className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded transition-colors"
                                                            title="Reverter movimentação"
                                                        >
                                                            {reverseLoading[mov.id] ? (
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                                                            ) : (
                                                                <FaUndo size={12} />
                                                            )}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {movimentacoesFiltradas.map((mov) => (
                            <MovimentacaoCard key={mov.id} movimentacao={mov} />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de Confirmação */}
            <ModalConfirmacao
                isOpen={modalConfirmacao.isOpen}
                onClose={fecharModalConfirmacao}
                onConfirm={reverterMovimentacao}
                movimentacao={modalConfirmacao.movimentacao}
                loading={reverseLoading[modalConfirmacao.movimentacao?.id]}
            />
        </>
    );
};

// Modal de Confirmação
const ModalConfirmacao = ({ isOpen, onClose, onConfirm, movimentacao, loading }) => {
    if (!isOpen) return null;

    const isEntrada = movimentacao?.tipo === 'entrada';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <FaExclamationTriangle className="text-orange-500" size={20} />
                            <h3 className="text-lg font-semibold text-gray-800">
                                Confirmar Reversão
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <FaTimes size={20} />
                        </button>
                    </div>

                    {/* Conteúdo */}
                    <div className="mb-6">
                        <p className="text-gray-600 mb-4">
                            Tem certeza que deseja reverter esta movimentação?
                        </p>

                        {/* Detalhes da movimentação */}
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Produto:</span>
                                <span className="font-semibold">{movimentacao?.produto_nome}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Tipo:</span>
                                <span className={`font-semibold ${isEntrada ? 'text-green-600' : 'text-red-600'}`}>
                                    {isEntrada ? 'Entrada' : 'Saída'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Quantidade:</span>
                                <span className="font-semibold">{movimentacao?.quantidade} unidades</span>
                            </div>
                            {movimentacao?.motivo && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Motivo:</span>
                                    <span className="font-semibold">{movimentacao.motivo}</span>
                                </div>
                            )}
                        </div>

                        {/* Aviso sobre o que acontecerá */}
                        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <p className="text-sm text-orange-800">
                                <strong>Atenção:</strong> Esta ação criará uma nova movimentação de{' '}
                                <span className="font-semibold">
                                    {isEntrada ? 'saída' : 'entrada'}
                                </span>{' '}
                                para reverter o efeito desta operação no estoque.
                            </p>
                        </div>
                    </div>

                    {/* Botões */}
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Revertendo...
                                </>
                            ) : (
                                <>
                                    <FaUndo size={14} />
                                    Reverter Movimentação
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockMovementReport;